import { useEffect, useState } from "react";
import { cn } from "../utils";
import { Button } from "./Button";

export interface CookieConsentConfig {
  enabled?: boolean;
  message?: string;
  acceptText?: string;
  declineText?: string;
  privacyPolicyUrl?: string;
  position?: "bottom" | "top";
}

export interface CookieConsentProps extends CookieConsentConfig {
  className?: string;
  storageKey?: string;
}

/**
 * Lightweight cookie consent banner. Opt-in via `enabled`.
 * Remembers choice in localStorage.
 */
export function CookieConsent({
  enabled = true,
  message = "We use cookies to improve your experience. By using this site, you agree to our use of cookies.",
  acceptText = "Accept",
  declineText = "Decline",
  privacyPolicyUrl,
  position = "bottom",
  className,
  storageKey = "sf-cookie-consent",
}: CookieConsentProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const stored = typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
    if (!stored) setVisible(true);
  }, [enabled, storageKey]);

  const handleChoice = (choice: "accepted" | "declined") => {
    localStorage.setItem(storageKey, choice);
    setVisible(false);
  };

  if (!enabled || !visible) return null;

  return (
    <div
      className={cn(
        "fixed left-4 right-4 z-50 md:left-auto md:right-6 md:max-w-xl shadow-lg rounded-[16px] border border-[color:var(--color-text,#0f172a)]/10 backdrop-blur-md",
        "bg-[var(--color-surface,#fff)] text-[var(--color-text,#0f172a)]",
        position === "bottom" ? "bottom-4 md:bottom-6" : "top-4 md:top-6",
        className
      )}
      role="dialog"
      aria-live="polite"
    >
      <div className="p-4 space-y-3">
        <p className="text-sm" style={{ color: "var(--color-text,#0f172a)", opacity: 0.9 }}>
          {message}
          {privacyPolicyUrl && (
            <>
              {" "}
              <a href={privacyPolicyUrl} className="sf-link font-semibold">
                Learn more
              </a>
            </>
          )}
        </p>
        <div className="flex gap-3 flex-wrap">
          <Button size="sm" onClick={() => handleChoice("accepted")} ripple>
            {acceptText}
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleChoice("declined")}>
            {declineText}
          </Button>
        </div>
      </div>
    </div>
  );
}
